import i18n from "i18next";
import type { TFunction } from "i18next";

type AuditCategories = {
  name: string;
  displayName: string;
  access: string;
};

type FieldChanges = {
  fieldName?: string;
  oldValue?: string | object | unknown[];
  newValue?: string | object | unknown[];
  fields?: Record<string, unknown>[];
  title?: string;
};

// A role field/value on an activity event is stored as the stable identifier (displayName,
// e.g. "SMB_OWNER"), not a resolved label, so it's looked up against the same flat `team:*`
// keys the Admin Activity page already has loaded. The equivalent `newMembers:ROLES.*` keys
// carry the same values, but that namespace is never fetched on this page — looking them up
// there silently falls back to the raw value instead of translating it (confirmed via a HAR
// capture: no newMembers.json request, but team.json is loaded). A custom role (or an entry
// older than the 30-day log retention window) has no matching key, so `t()`'s `defaultValue`
// falls back to the raw value instead of leaving the message blank. `role`/`oldRole.displayName`
// is always a single scalar here -- both perimeter81-api dispatch paths (User.js#toAuditRole and
// MemberController.js) set it from one Role doc, never an array.
export function resolveRoleLabel(t: TFunction, value: unknown): unknown {
  if (typeof value !== "string") return value;
  return t(`team:${value}`, { defaultValue: value });
}

function activityLogNameMapper(
  categories: AuditCategories[]
): Omit<AuditCategories, "displayName">[] {
  return categories.map(({ displayName, ...rest }) => ({
    ...rest,
    name: i18n.t(`customRoles:CATEGORY_NAMES.${rest.name}`, {
      defaultValue: displayName,
    }),
  }));
}

export function customRoleNameMapper(fieldChanges: FieldChanges[]): void {
  for (const change of fieldChanges) {
    if (change.fieldName === "permissions") {
      if (Array.isArray(change.oldValue)) {
        change.oldValue = activityLogNameMapper(change.oldValue as AuditCategories[]);
      }
      if (Array.isArray(change.newValue)) {
        change.newValue = activityLogNameMapper(change.newValue as AuditCategories[]);
      }
    }
  }
}